import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { postMessageRN, useReactNativeProps, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { LoginMethodType } from '@eika-infrastruktur/mobilbank-lib/src/nativeProps';
import { ErrorMessage } from '@eika/attention-message';
import { Button, LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import useTranslation, { format } from '../../hooks/useTranslation';
import { settingsTrackIDs } from '../../utils/gtm/trackIDs/settings';
import SelectPanel from './SelectPanel';

import './SelectLoginMethod.scss';

const SelectLoginMethod = () => {
    const { biometrics, loginMethod } = useReactNativeProps();

    const navigate = useNavigate();
    const translated = useTranslation();
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [navigateToNextStep, setNavigateToNextStep] = useState(false);
    const [loginMethodChosen, setLoginMethodChosen] = useState<LoginMethodType | undefined>(loginMethod?.type);
    const [noLoginMethodChosen, setNoLoginMethodChosen] = useState<boolean>(false);

    const navigateToChooseSigning = () => {
        if (!loginMethodChosen) {
            setNoLoginMethodChosen(true);
        } else if (loginMethodChosen === 'BIOMETRIC' && !biometrics?.biometricsEnabledOS) {
            setShowErrorMessage(true);
            setNoLoginMethodChosen(false);
        } else {
            setNoLoginMethodChosen(false);
            if (loginMethod?.type !== loginMethodChosen) {
                postMessageRN({ action: WebToRNAction.SET_LOGIN_METHOD, payload: loginMethodChosen });
            }
            setNavigateToNextStep(true);
        }
    };

    useEffect(() => {
        if (loginMethod?.type === loginMethodChosen && navigateToNextStep) {
            navigate('/signing');
        }
    }, [navigateToNextStep, loginMethod, loginMethodChosen, navigate]);

    return (
        <>
            <div className="login-method-row">
                <h2 className="login-method-subtitle">{translated.loginAndSigning.chooseLogin}</h2>
                <span className="login-method-step">{format(translated.loginAndSigning.step, 1)}</span>
            </div>
            <dt className="login-method-description">{translated.loginAndSigning.chooseLoginMethodDescription}</dt>
            <SelectPanel
                isSelected={loginMethodChosen === 'PIN'}
                icon={IconTypes.PIN_KODE_36}
                onClick={() => setLoginMethodChosen('PIN')}
                label={translated.loginAndSigning.signingMethods.PIN}
                trackId={settingsTrackIDs.loginMethodLink}
            />
            <SelectPanel
                isSelected={
                    (!loginMethodChosen && biometrics?.biometricsEnabledApp) || loginMethodChosen === 'BIOMETRIC'
                }
                icon={IconTypes.BIOMETRI_36}
                onClick={() => setLoginMethodChosen('BIOMETRIC')}
                label={translated.loginAndSigning.signingMethods.BIOMETRIC}
                trackId={settingsTrackIDs.loginMethodLink}
            />
            {showErrorMessage && (
                <ErrorMessage
                    text={translated.loginAndSigning.errorMessageBiometryDisabled}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}
            {loginMethod?.error && (
                <ErrorMessage text={translated.loginAndSigning.errorMessage} icon={IconTypes.UTROPSTEGN_24} />
            )}
            {noLoginMethodChosen && (
                <ErrorMessage
                    text={translated.loginAndSigning.errorMessageNoLoginMethodChosen}
                    icon={IconTypes.UTROPSTEGN_24}
                />
            )}
            <div className="login-method-button-parent">
                <Button className="signing-method-button" onClick={navigateToChooseSigning}>
                    {translated.loginAndSigning.saveAndContinue}
                </Button>
                <LinkButton className="signing-method-button" onClick={() => navigate(-1)}>
                    {translated.cancel}
                </LinkButton>
            </div>
        </>
    );
};

export default SelectLoginMethod;
